import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import NavSelect from '@/components/select/NavSelect';
import AppContext from '@/contexts/appContext';
import { useNarrowNav } from '@/contexts/narrowNavContext';
import PersonContext from '@/pages/Person/PersonContext';
import { getPersonNavOptions } from '@/utils/personNavOptions';

import type { Props } from './types';

/**
 * The person's pages as a `<select>`, the narrow layout's original navigation.
 *
 * Superseded by the drawer behind the top bar's menu button, and rendered only for people who
 * have asked for the dropdown back in Dugout Settings.
 */
const PersonNav = ({ defaultOptionLabel = '...' }: Props) => {
  const { navStyle } = useNarrowNav();
  const { permissions } = useContext(AppContext);
  const {
    person: { id: personId }
  } = useContext(PersonContext);
  const location = useLocation();

  const navOptions: { [key: string]: string } = useMemo(
    () =>
      Object.fromEntries(
        getPersonNavOptions(personId, permissions!).map(({ label, path }) => [label, path])
      ),
    [permissions, personId]
  );

  const selectedValue = useMemo(() => {
    const match = Object.entries(navOptions).find((x) => x[1] === location.pathname);

    if (match) {
      return match[0];
    }

    if (
      location.pathname === `/people/${personId}` ||
      location.pathname === `/people/${personId}/`
    ) {
      return 'Profile';
    }

    return 'default';
  }, [location, navOptions, personId]);

  if (navStyle !== 'select') return null;

  return (
    <div className="flex items-center justify-between flex-row">
      <NavSelect
        navOptions={navOptions}
        selectedValue={selectedValue}
        defaultOption={{ label: defaultOptionLabel, value: 'default' }}
      />
    </div>
  );
};

export default PersonNav;
